import { loggingDb } from './logDb';
import { generateUUID } from '@/modules/uuid';
import type { LogEntry, LogLevel, OutboxRow } from './types';

export const ENVELOPE_VERSION = 1;

export const MAX_RECORD_BYTES = 8 * 1024;

export const MAX_MESSAGE_LENGTH = 512;
export const MAX_LABEL_LENGTH = 128;

// Denied by the collector by name.
const LOCAL_ONLY_CONTEXT_KEYS = ['referrer'];

// Must outlast the drain's request timeout, or a live request is reclaimed.
export const LEASE_MS = 2 * 60 * 1000;

export const BEACON_MIRROR_SIZE = 10;

type WireRecord = OutboxRow['record'];

// pagehide cannot wait on IndexedDB.
let beaconMirror: OutboxRow[] = [];

export function shipsAt(level: LogLevel, diagnosticMode: boolean): boolean {
    return level === 'ERROR' || level === 'WARN' || (diagnosticMode && level === 'INFO');
}

// TextEncoder is absent under jsdom.
export function utf8Length(text: string): number {
    let bytes = 0;

    for (let i = 0; i < text.length; i++) {
        const code = text.charCodeAt(i);

        if (code < 0x80) {
            bytes += 1;
        }
        else if (code < 0x800) {
            bytes += 2;
        }
        else if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length
            && text.charCodeAt(i + 1) >= 0xdc00 && text.charCodeAt(i + 1) <= 0xdfff) {
            bytes += 4;
            i++;
        }
        else {
            bytes += 3;
        }
    }

    return bytes;
}

function serialisedBytes(value: unknown): number | undefined {
    try {
        return utf8Length(JSON.stringify(value));
    }
    catch {
        return undefined;
    }
}

function fits(value: unknown): boolean {
    const size = serialisedBytes(value);
    return size !== undefined && size <= MAX_RECORD_BYTES;
}

function shippableContext(context: unknown): LogEntry['context'] {
    if (!context || typeof context !== 'object' || Array.isArray(context)) {
        return {};
    }

    const kept = { ...(context as LogEntry['context']) };
    LOCAL_ONLY_CONTEXT_KEYS.forEach((key) => delete kept[key]);

    return kept;
}

function plainRecord(record: WireRecord): WireRecord {
    const plain: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(record)) {
        if (key === 'context') {
            continue;
        }

        if (typeof value === 'string') {
            plain[key] = value.slice(0, key === 'message' ? MAX_MESSAGE_LENGTH : MAX_LABEL_LENGTH);
        }
        else if (typeof value === 'boolean' || Number.isFinite(value)) {
            plain[key] = value;
        }
    }

    return { ...plain, context: record.context } as WireRecord;
}

// Every row must serialise and fit, or it heads every batch.
export function wireRecord(entry: LogEntry): WireRecord {
    const { id: _id, ...rest } = entry;
    const record = { ...rest, context: shippableContext(rest.context) };

    if (fits(record)) {
        return record;
    }

    const trimmed = typeof record.message === 'string'
        ? { ...record, message: record.message.slice(0, MAX_MESSAGE_LENGTH) }
        : record;

    if (fits(trimmed)) {
        return trimmed;
    }

    const plain = plainRecord(trimmed);

    if (fits(plain)) {
        return plain;
    }

    const contextBytes = serialisedBytes(record.context);

    return { ...plain, context: contextBytes === undefined ? {} : { contextBytes } };
}

export function payload(row: OutboxRow): Record<string, unknown> {
    return { ...row.record, id: row.uuid };
}

export function envelope(rows: OutboxRow[]): string {
    return JSON.stringify({ v: ENVELOPE_VERSION, records: rows.map(payload) });
}

function sendable(row: OutboxRow): boolean {
    return serialisedBytes(payload(row)) !== undefined;
}

// Always takes the first row, so an oversized one is sent and answered.
export function selectWithin(rows: OutboxRow[], maxRecords: number, maxBytes: number) {
    const selected: OutboxRow[] = [];
    let bytes = utf8Length(envelope([]));

    for (const row of rows.filter(sendable).slice(0, maxRecords)) {
        const size = utf8Length(JSON.stringify(payload(row))) + (selected.length > 0 ? 1 : 0);

        if (selected.length > 0 && bytes + size > maxBytes) {
            break;
        }

        selected.push(row);
        bytes += size;
    }

    return selected;
}

async function evictOverCap(cap: number): Promise<number[]> {
    const excess = (await loggingDb.outbox.count()) - cap;

    if (excess <= 0) {
        return [];
    }

    const byAge = (a: number, b: number) => a - b;
    const nonError = await loggingDb.outbox.where('level').notEqual('ERROR').primaryKeys();
    const victims = nonError.sort(byAge).slice(0, excess);

    if (victims.length < excess) {
        const errors = await loggingDb.outbox.where('level').equals('ERROR').primaryKeys();
        victims.push(...errors.sort(byAge).slice(0, excess - victims.length));
    }

    await loggingDb.outbox.bulkDelete(victims);

    return victims;
}

// Call inside the transaction that writes the log row.
export async function enqueue(entry: LogEntry, cap: number, now = Date.now()): Promise<OutboxRow> {
    const row: OutboxRow = {
        uuid: generateUUID(),
        level: entry.level,
        state: 'pending',
        attempts: 0,
        createdAt: now,
        record: wireRecord(entry),
    };

    row.id = await loggingDb.outbox.add(row);
    forget(await evictOverCap(cap));

    return row;
}

// Call after the transaction commits.
export function mirror(row: OutboxRow): void {
    if (row.level !== 'ERROR') {
        return;
    }

    beaconMirror = [...beaconMirror, row].slice(-BEACON_MIRROR_SIZE);
}

function forget(ids: number[]): void {
    if (ids.length > 0) {
        const gone = new Set(ids);
        beaconMirror = beaconMirror.filter((row) => !gone.has(row.id as number));
    }
}

export function beaconRows(): OutboxRow[] {
    return [...beaconMirror];
}

export async function claimBatch(
    maxRecords: number,
    maxBytes: number,
    now = Date.now(),
): Promise<OutboxRow[]> {
    return loggingDb.transaction('rw', loggingDb.outbox, async () => {
        let batch: OutboxRow[] = [];

        for (;;) {
            const pending = await loggingDb.outbox
                .where('state')
                .equals('pending')
                .limit(maxRecords)
                .toArray();
            const poison = pending.filter((row) => !sendable(row)).map((row) => row.id as number);

            if (poison.length > 0) {
                await loggingDb.outbox.bulkDelete(poison);
            }

            batch = selectWithin(pending, maxRecords, maxBytes);

            if (batch.length > 0 || poison.length === 0) {
                break;
            }
        }

        const ids = batch.map((row) => row.id as number);

        if (ids.length > 0) {
            await loggingDb.outbox.where('id').anyOf(ids).modify({
                state: 'inflight',
                claimedAt: now,
            });
        }

        return batch.map((row) => ({ ...row, state: 'inflight', claimedAt: now }));
    });
}

export async function settle(ids: number[]): Promise<void> {
    await loggingDb.outbox.bulkDelete(ids);
    forget(ids);
}

// A lapsed lease may already have been reclaimed by another tab.
export async function release(ids: number[], claimedAt: number, failed: boolean): Promise<void> {
    await loggingDb.outbox
        .where('id')
        .anyOf(ids)
        .filter((row) => row.state === 'inflight' && row.claimedAt === claimedAt)
        .modify((row) => {
            row.state = 'pending';
            delete row.claimedAt;
            if (failed) {
                row.attempts += 1;
            }
        });
}

// Both directions: the device clock may have been corrected since the claim.
export async function recoverStranded(now = Date.now(), leaseMs = LEASE_MS): Promise<number> {
    return loggingDb.outbox
        .where('state')
        .equals('inflight')
        .filter((row) => Math.abs(now - (row.claimedAt ?? 0)) > leaseMs)
        .modify((row) => {
            row.state = 'pending';
            delete row.claimedAt;
        });
}

export function resetBeaconMirrorForTests(): void {
    beaconMirror = [];
}
